import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { ICountry } from '@shared/shared/interfaces/country.interfaces';
import { IMemberProfilePhoneNumber } from '@shared/shared/interfaces/member-profile-phone-number.interfaces';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { CountryService } from '@shared/shared/services/country.service';
import * as MemberProfileStore from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, Subject, combineLatest, from } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ten-phone-number-form',
  templateUrl: './phone-number-form.component.html',
  styleUrls: ['../popup-form-base.scss', './phone-number-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyOptionModule,
    ErrorDirective,
    ErrorsDirective,
    AsyncPipe,
    NgFor,
    NgClass,
    MatLegacyInputModule,
    MatLegacyCheckboxModule,
    MatLegacyButtonModule,
    AccessibleClickDirective,
    MatLegacyProgressSpinnerModule,
  ],
})
export class PhoneNumberFormComponent implements OnInit, OnDestroy {
  @Input() public memberUrl: string;
  @Input() public pastDataId?: any;

  @Output() public closed = new EventEmitter<any>();

  public countries$: Observable<Array<ICountry>> = this.countryService.getCountries();

  public member_profile_phone_numbers$: Observable<IMemberProfilePhoneNumber[]> = this.ngrxStore.select(
    MemberProfileStore.getMemberProfilePhoneNumberFeatureState
  );

  public newPhoneNumber: boolean;
  public phoneNumberForm: UntypedFormGroup;
  public phone_number$: Observable<IMemberProfilePhoneNumber>;
  public selectedCountry: any;

  public countries: Array<ICountry>;
  private settings: ISiteSettings;
  public customVariables$: Observable<CustomisableVariables> = this.customVariablesService.variables;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private form_builder: UntypedFormBuilder,
    private siteSettingsService: SiteSettingsService,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private countryService: CountryService,
    private customVariablesService: CustomisableVariablesService,
    private angulartics2Segment: Angulartics2Segment,
    private cdr: ChangeDetectorRef
  ) {
    this.phoneNumberForm = this.form_builder.group({
      preferred: [false],
      type: ['', Validators.required],
      number: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      country_calling_code: [],
      country: ['', Validators.required],
    });
  }

  public ngOnInit() {
    this.newPhoneNumber = this.pastDataId === undefined;

    if (!this.newPhoneNumber) {
      this.phone_number$ = from(
        this.member_profile_phone_numbers$.pipe(
          take(1),
          map((data) => {
            const single_number = data.filter((number) => number.id === this.pastDataId)[0];
            this.obtainPhoneData(single_number);
            return single_number;
          })
        )
      );
    } else {
      combineLatest(this.siteSettingsService.fetchSiteSettings(), this.countries$)
        .pipe(
          filter(([settings]) => settings !== null),
          distinctUntilChanged(),
          takeUntil(this.unsubscribe$)
        )
        .subscribe(([settings, countries]) => {
          this.settings = settings;
          this.countries = countries;
          this.cdr.markForCheck();

          this.resetFormData();
        });
    }
  }

  /**
   * Reset the form to initial empry state
   */
  public resetFormData() {
    this.phoneNumberForm.get('preferred').setValue(false);
    this.phoneNumberForm.get('type').setValue('');
    this.phoneNumberForm.get('number').setValue('');

    this.phoneNumberForm.get('country_calling_code').setValue('');
    this.phoneNumberForm.get('country').setValue(undefined);

    if (this.countries && this.countries.length) {
      const selectedCountry = this.countries.find((country) => country.url === this.settings.main_country);
      if (this.selectCountry) {
        this.selectCountry(selectedCountry);
      }
    }
  }

  /**
   * Obtain phone number data based on ID passed as input to this component. Cycles through countries data to
   * find a matching record to display full country code data. Called if phone number ID passed to this component.
   * @param pastPhoneData - The phone data the user wishes to edit
   */
  public obtainPhoneData(pastPhoneData: IMemberProfilePhoneNumber) {
    this.countries$
      .pipe(
        filter((value) => value.length > 0),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((countries) => {
        this.restoreFormData(pastPhoneData);
        countries.forEach((country) => {
          if (country && country.calling_code_url === pastPhoneData.country_calling_code) {
            this.selectCountry(country);
          }
        });
      });
  }

  /**
   * Sets the value of the form fields if form is used to edit a previously saved phone number. Called from
   * the 'obtainPhoneData' function
   * @param data - Phone number data to be edited
   */
  public restoreFormData(data: IMemberProfilePhoneNumber) {
    if (this.phoneNumberForm) {
      this.phoneNumberForm.get('type').setValue(data.type);
      this.phoneNumberForm.get('preferred').setValue(data.preferred);
      this.phoneNumberForm.get('number').setValue(data.number);

      // If data is returned without calling code data
      if (data && data.country_calling_code_data && data.country_calling_code) {
        this.phoneNumberForm.get('country').setValue(data);
        this.phoneNumberForm.get('country_calling_code').setValue(data.country_calling_code_data.code);
      }
    }
  }

  /**
   * Set the chosen country. Called when the user selects a country from the country calling code
   * options list.
   * @param country - ICountry object used by the select component
   */
  public selectCountry(country: ICountry) {
    this.selectedCountry = country;
    this.phoneNumberForm.get('country').setValue(country);
    this.phoneNumberForm.get('country_calling_code').setValue(country.calling_code);
  }

  public selectCountryCallingCode(event: any) {
    this.selectCountry(event.value);
  }

  /**
   * Close the phone number form by emitting a value that is caught by the popupFormComponent. Called when the user
   * clicks onto the close button.
   */
  public closeForm() {
    this.closed.emit();
  }

  /**
   * Dispatch the relevant action to delete the current phone number. Called when the user clicks onto the
   * remove button.
   */
  public removePhoneNumber() {
    this.ngrxStore.dispatch(new MemberProfileStore.DestroyProfilePhoneNumber(this.pastDataId));
    this.closeForm();
  }

  /**
   * Checks for form validation before dispatching the relevant action to either add or update a phone number.
   * Called when the user clicks onto the submit button.
   */
  public submitForm() {
    this.angulartics2Segment.eventTrack(
      this.newPhoneNumber ? 'My profile user add new phone number' : 'My profile user edit existing phone number',
      {
        type: this.phoneNumberForm.get('type').value,
        preferred: this.phoneNumberForm.get('preferred').value,
      }
    );

    if (this.phoneNumberForm.valid) {
      const data = this.phoneNumberForm.value;
      this.closeForm();

      data.country_calling_code = data.country.calling_code_url;
      delete data.country_calling_code_url;

      if (!data.preferred) {
        delete data.preferred;
      }

      if (this.newPhoneNumber) {
        data['member_profile'] = this.memberUrl;
        this.ngrxStore.dispatch(new MemberProfileStore.CreateProfilePhoneNumber(data));
      } else {
        this.ngrxStore.dispatch(new MemberProfileStore.UpdateProfilePhoneNumber({ data, id: this.pastDataId }));
      }
    } else {
      Object.keys(this.phoneNumberForm.controls).forEach((field) => {
        this.phoneNumberForm.controls[field].markAsTouched();
      });
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
